import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IoArrowBackCircleOutline, IoChevronDown } from 'react-icons/io5';
import { Button } from './Button';
import { GroupBuyRequest, IConfig } from '../config/interfaces';
import TextInput from './TextInput';
import { getPrimaryBuyerLabel } from '../config/constants';
import { City, Country, State } from 'country-state-city';

interface DeliveryOptionProps {
	description: string;
	id: string;
	label: string;
	steps: string[];
	icon: React.ReactNode;
}

interface DeliveryPageOptions {
	onChange: (field: string, value: any) => void;
	onProceed: () => void;
	goBack?: () => void;
	groupBuySettings?: GroupBuyRequest;
	buttonConfig?: string;
	isLoading?: boolean;
	config: IConfig;
}
const Delivery: React.FC<DeliveryPageOptions> = ({
	onChange,
	isLoading,
	onProceed,
	goBack,
	buttonConfig,
	groupBuySettings,
	config,
}) => {
	const [countryError, setCountryError] = useState('');
	const [geocodeError, setGeocodeError] = useState('');
	const [isGeocoding, setIsGeocoding] = useState(false);

	type DropdownOption = { label: string; value: string };

	const DropdownField: React.FC<{
		label: string;
		value: string;
		placeholder: string;
		options: DropdownOption[];
		onSelect: (value: string) => void;
		disabled?: boolean;
	}> = ({ label, value, placeholder, options, onSelect, disabled }) => {
		const [open, setOpen] = useState(false);
		const [searchTerm, setSearchTerm] = useState('');
		const containerRef = useRef<HTMLDivElement | null>(null);

		useEffect(() => {
			const handleClickOutside = (event: MouseEvent) => {
				if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
					setOpen(false);
				}
			};

			document.addEventListener('mousedown', handleClickOutside);

			return () => document.removeEventListener('mousedown', handleClickOutside);
		}, []);

		useEffect(() => {
			if (disabled) {
				setOpen(false);
			}
		}, [disabled]);

		useEffect(() => {
			if (!open) {
				setSearchTerm('');
			}
		}, [open]);

		const selectedLabel = options.find((option) => option.value === value)?.label || '';
		const displayValue = open ? searchTerm : selectedLabel;
		const filteredOptions = open
			? options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
			: options;

		return (
			<div className="w-full mb-4 relative" ref={containerRef}>
				<label className="text-lg font-medium mb-2 block">{label}</label>
				<div className="relative">
					<input
						type="text"
						value={displayValue}
						placeholder={open && selectedLabel ? selectedLabel : placeholder}
						onFocus={(event) => {
							if (disabled) return;
							setSearchTerm('');
							setOpen(true);
							event.currentTarget.select();
						}}
						onChange={(event) => {
							if (disabled) return;
							const nextValue = event.target.value;
							setSearchTerm(nextValue);
							if (!open) setOpen(true);
						}}
						onKeyDown={(event) => {
							if (event.key === 'Escape') {
								setOpen(false);
								event.currentTarget.blur();
							}
						}}
						autoComplete="off"
						disabled={disabled}
						className={`rounded-lg border border-gray-300 text-gray-900 text-sm px-2.5 py-2.5 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent ${
							disabled ? 'bg-gray-100 opacity-70 cursor-not-allowed' : 'bg-gray-50 cursor-text'
						}`}
					/>
					<IoChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
				</div>
				{open && (
					<div className="absolute z-40 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow">
						{filteredOptions.length === 0 ? (
							<p className="px-3 py-2 text-sm text-gray-500">No options available</p>
						) : (
							filteredOptions.map((option, index) => (
								<div
									key={`${option.value}-${index}`}
									role="option"
									onClick={() => {
										onSelect(option.value);
										setOpen(false);
										setSearchTerm('');
									}}
									onKeyDown={(event) => {
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault();
											onSelect(option.value);
											setOpen(false);
											setSearchTerm('');
										}
									}}
									tabIndex={0}
									className={`px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
										option.value === value ? 'bg-gray-100 font-medium' : ''
									}`}
								>
									{option.label}
								</div>
							))
						)}
					</div>
				)}
			</div>
		);
	};

	const countries = useMemo(() => Country.getAllCountries(), []);

	const [address, setAddress] = useState({
		country: '',
		city: '',
		postal_code: '',
		province: '',
		longitude: '',
		latitude: '',
		address: '',
	});

	const canShip =
		groupBuySettings?.has_international_shipping ||
		groupBuySettings?.has_local_shipping ||
		groupBuySettings?.has_shipping;

	const [deliveryOption, setDeliveryOption] = useState<string>(canShip ? 'shipping' : 'pickup');

	const [selectedCountryIso, setSelectedCountryIso] = useState('');
	const [selectedStateIso, setSelectedStateIso] = useState('');

	const states = useMemo(() => {
		if (!selectedCountryIso) return [];
		return State.getStatesOfCountry(selectedCountryIso);
	}, [selectedCountryIso]);

	const cities = useMemo(() => {
		if (!selectedCountryIso || !selectedStateIso) return [];
		return City.getCitiesOfState(selectedCountryIso, selectedStateIso);
	}, [selectedCountryIso, selectedStateIso]);

	const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setDeliveryOption(event.target.value);
		onChange('delivery_method', event.target.value);
		setCountryError('');
		setGeocodeError('');
		setIsGeocoding(false);
	};

	const normalizeCountry = (value: string) => {
		if (!value) {
			return { isoCode: '', name: '' };
		}
		const upperValue = value.toUpperCase();
		const byIso = countries.find((country) => country.isoCode.toUpperCase() === upperValue);
		if (byIso) {
			return { isoCode: byIso.isoCode.toUpperCase(), name: byIso.name.toLowerCase() };
		}

		const byName = countries.find((country) => country.name.toLowerCase() === value.toLowerCase());
		if (byName) {
			return { isoCode: byName.isoCode.toUpperCase(), name: byName.name.toLowerCase() };
		}

		return { isoCode: upperValue, name: value.toLowerCase() };
	};

	const validateCountry = (countryValue: string) => {
		if (!countryValue) return '';
		if (!groupBuySettings?.has_international_shipping) {
			const businessCountry = groupBuySettings?.business?.country || '';
			const selected = normalizeCountry(countryValue);
			const business = normalizeCountry(businessCountry);

			if (selected.isoCode && business.isoCode) {
				if (selected.isoCode !== business.isoCode) {
					return 'International shipping is not allowed';
				}
			} else if (selected.name && business.name && selected.name !== business.name) {
				return 'International shipping is not allowed';
			}
		}
		return '';
	};

	const handleAddressChange = (field: keyof typeof address, value: string) => {
		setAddress((prev) => {
			const updatedAddress = { ...prev, [field]: value };
			setCountryError(validateCountry(updatedAddress.country));
			if (field === 'address' || field === 'city' || field === 'province' || field === 'country' || field === 'postal_code') {
				setGeocodeError('');
			}
			return updatedAddress;
		});
		onChange(field, value);
	};

	const deliveryOptions: DeliveryOptionProps[] = useMemo(() => {
		const businessAddress = groupBuySettings?.business?.business_address || '';
		const options = [
			{
				id: 'pickup',
				label: 'Pick up',
				steps: [] as string[],
				description: `${businessAddress} `,
				icon: <></>,
			},
		];

		if (canShip && groupBuySettings) {
			options.unshift({
				id: 'shipping',
				label: 'Ship',
				description: '',
				steps: [
					`Local shipping is ${groupBuySettings.has_local_shipping ? '' : 'not'} allowed`,
					`Standard shipping is ${groupBuySettings.has_shipping ? '' : 'not'} allowed`,
					`International shipping is ${groupBuySettings.has_international_shipping ? '' : 'not'} allowed`,
				],
				icon: <></>,
			});
		}
		return options;
	}, [canShip, groupBuySettings]);

	const brandColor = groupBuySettings?.brand_color;

	const requiredAddressFields: (keyof typeof address)[] = ['address', 'city', 'province', 'postal_code', 'country', 'latitude', 'longitude'];

	const disabled =
		!deliveryOption ||
		(deliveryOption === 'shipping' &&
			(requiredAddressFields.some((field) => !address[field]) || Boolean(countryError) || Boolean(geocodeError) || isGeocoding));

	const handleCountrySelect = (countryIso: string) => {
		setSelectedCountryIso(countryIso);
		setSelectedStateIso('');

		handleAddressChange('country', countryIso || '');
		handleAddressChange('province', '');
		handleAddressChange('city', '');
	};

	const handleStateSelect = (stateIso: string) => {
		setSelectedStateIso(stateIso);
		const state = states.find((item) => item.isoCode === stateIso);
		handleAddressChange('province', state?.name || '');
		handleAddressChange('city', '');
	};

	const handleCitySelect = (cityName: string) => {
		handleAddressChange('city', cityName);
	};

	useEffect(() => {
		if (deliveryOption !== 'shipping') {
			return;
		}

		const street = address.address.trim();
		const city = address.city.trim();
		const postalCode = address.postal_code.trim();
		const countryIso = selectedCountryIso.trim();

		const hasMinimumAddress = Boolean(street && city && countryIso);

		if (!hasMinimumAddress) {
			setIsGeocoding(false);
			setGeocodeError('');
			if (address.latitude || address.longitude) {
				setAddress((prev) => {
					if (!prev.latitude && !prev.longitude) return prev;
					return { ...prev, latitude: '', longitude: '' };
				});
				if (address.latitude) onChange('latitude', '');
				if (address.longitude) onChange('longitude', '');
			}
			return;
		}

		const selectedCountry = countries.find((country) => country.isoCode === countryIso);

		const params = new URLSearchParams({
			format: 'json',
			limit: '1',
			street,
			city,
		});

		if (address.province.trim()) {
			params.append('state', address.province.trim());
		}

		if (postalCode) {
			params.append('postalcode', postalCode);
		}

		if (selectedCountry?.name) {
			params.append('country', selectedCountry.name);
		}

		const controller = new AbortController();
		const debounceId = setTimeout(() => {
			setIsGeocoding(true);
			setGeocodeError('');

			if (address.latitude || address.longitude) {
				setAddress((prev) => ({ ...prev, latitude: '', longitude: '' }));
				if (address.latitude) onChange('latitude', '');
				if (address.longitude) onChange('longitude', '');
			}

			// Use OpenStreetMap Nominatim for geocoding; keep requests throttled to respect usage policy.
			fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
				signal: controller.signal,
				headers: { Accept: 'application/json' },
			})
				.then((response) => response.json())
				.then((data) => {
					if (controller.signal.aborted) return;

					if (!Array.isArray(data) || !data.length || !data[0]?.lat || !data[0]?.lon) {
						setGeocodeError('Unable to retrieve coordinates for this address');
						return;
					}

					const { lat, lon } = data[0];
					setAddress((prev) => ({ ...prev, latitude: lat, longitude: lon }));
					onChange('latitude', lat);
					onChange('longitude', lon);
					setGeocodeError('');
				})
				.catch(() => {
					if (controller.signal.aborted) return;
					setGeocodeError('Unable to retrieve coordinates for this address');
				})
				.finally(() => {
					if (!controller.signal.aborted) {
						setIsGeocoding(false);
					}
				});
		}, 600);

		return () => {
			clearTimeout(debounceId);
			controller.abort();
		};
	}, [address.address, address.city, address.province, address.postal_code, selectedCountryIso, deliveryOption, countries]);

	return (
		<>
			<div className="p-2">
				<div onClick={() => goBack && goBack()} className="cursor-pointer flex items-center gap-2 mb-4">
					<IoArrowBackCircleOutline />
					<p className="text-sm font-semibold">Go Back</p>
				</div>
				<h2 className="text-lg font-semibold my-2">Fullfillment Preference for {getPrimaryBuyerLabel(config)}</h2>

				<ul className="w-full">
					{deliveryOptions.map((option) => (
						<li className="w-full my-4">
							<input
								type="radio"
								id={option.id}
								name="hosting"
								checked={deliveryOption === option.id}
								onChange={handleOptionChange}
								value={option.id}
								className="hidden peer"
								required
							/>
							<label
								htmlFor={option.id}
								// style={{}}
								className={`inline-flex items-center justify-between w-full p-5 text-gray-500 bg-white border border-gray-700 rounded-lg cursor-pointer  peer-checked:border-[${brandColor}] peer-checked:text-[${brandColor}] hover:text-gray-600 hover:bg-gray-100 `}
							>
								<div className="block">
									<div className="w-full text-lg font-semibold">{option.label}</div>
									<div className="">
										{option.steps.map((step) => (
											<div className="flex flex-row items-center gap-1">
												<span className="text-red-500">*</span>
												<p className="text-xs text-gray-500">{step}</p>
											</div>
										))}
									</div>
									<div className="w-full text-sm capitalize">{option.description}</div>
								</div>
								<svg
									className="w-5 h-5 ms-3 rtl:rotate-180"
									aria-hidden="true"
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 14 10"
								>
									<path
										stroke="currentColor"
										strokeLinecap="round"
										stroke-linejoin="round"
										strokeWidth="2"
										d="M1 5h12m0 0L9 1m4 4L9 9"
									/>
								</svg>
							</label>
						</li>
					))}
				</ul>

				{deliveryOption === 'shipping' && (
					<div className="mt-3">
						<h2 className="text-lg font-semibold mb-2">Shipping Address</h2>
						<TextInput
							value={address.address}
							onChange={(value) => handleAddressChange('address', value)}
							placeholder="Street Address"
							className="w-full"
							label="Street Address"
						/>
						<DropdownField
							label="Country"
							value={selectedCountryIso}
							placeholder="Select Country"
							options={countries.map((country) => ({
								label: country.name,
								value: country.isoCode,
							}))}
							onSelect={handleCountrySelect}
						/>
						{states.length > 0 ? (
							<DropdownField
								label="State / Province"
								value={selectedStateIso}
								placeholder="Select State / Province"
								options={states.map((state) => ({
									label: state.name,
									value: state.isoCode,
								}))}
								onSelect={handleStateSelect}
								disabled={!selectedCountryIso}
							/>
						) : (
							<TextInput
								value={address.province}
								onChange={(value) => handleAddressChange('province', value)}
								placeholder="State / Province"
								className="w-full"
								label="State / Province"
							/>
						)}
						{cities.length > 0 ? (
							<DropdownField
								label="City"
								value={address.city}
								placeholder="Select City"
								options={cities.map((city) => ({
									label: city.name,
									value: city.name,
								}))}
								onSelect={handleCitySelect}
								disabled={!selectedStateIso}
							/>
						) : (
							<TextInput
								value={address.city}
								onChange={(value) => handleAddressChange('city', value)}
								placeholder="City"
								className="w-full"
								label="City"
							/>
						)}
						<TextInput
							value={address.postal_code}
							tooltip="Enter Postcode"
							onChange={(value) => handleAddressChange('postal_code', value)}
							placeholder="Postal Code"
							className="w-full"
							label="Postal Code"
						/>
						{isGeocoding && <p className="text-xs text-gray-500 mb-2">Retrieving coordinates…</p>}
					</div>
				)}
				{countryError && <p className="text-red-500 text-xs">{countryError}</p>}
				{geocodeError && <p className="text-red-500 text-xs">{geocodeError}</p>}
			</div>

			<Button
				title={'Proceed to pay'}
				className={buttonConfig || 'bg-indigo-600 text-white hover:bg-indigo-700'}
				disabled={disabled}
				isLoading={isLoading}
				onClick={(event) => {
					onProceed();
				}}
			/>
		</>
	);
};

export default Delivery;
